import "@/App.css";
import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageLoader } from "@/components/Shared";

import Landing from "@/pages/Landing";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import StoreKit from "@/pages/StoreKit";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import CheckIn from "@/pages/CheckIn";
import Train from "@/pages/Train";
import Body from "@/pages/Body";
import Progress from "@/pages/Progress";
import Nutrition from "@/pages/Nutrition";
import Calendar from "@/pages/Calendar";
import Coach from "@/pages/Coach";
import Profile from "@/pages/Profile";
import Trainer from "@/pages/Trainer";
import Chat from "@/pages/Chat";
import Sleep from "@/pages/Sleep";
import Cardio from "@/pages/Cardio";
import Achievements from "@/pages/Achievements";
import Feed from "@/pages/Feed";
import Friends from "@/pages/Friends";
import Share from "@/pages/Share";
import History from "@/pages/History";

function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        if (sid) await api.post("/auth/session", { session_id: sid });
        window.history.replaceState(null, "", "/dashboard");
        const u = await refresh();
        navigate(u?.onboarded ? "/dashboard" : "/onboarding", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, refresh]);

  return <PageLoader />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user && user.onboarded === false) return <Navigate to="/onboarding" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <PageLoader />;
  if (user) return <Navigate to={user.onboarded ? "/dashboard" : "/onboarding"} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/share/:token" element={<Share />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/checkin" element={<CheckIn />} />
        <Route path="/history" element={<History />} />
        <Route path="/train" element={<Train />} />
        <Route path="/body" element={<Body />} />
        <Route path="/progress" element={<Progress />} />
        <Route path="/nutrition" element={<Nutrition />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/coach" element={<Coach />} />
        <Route path="/sleep" element={<Sleep />} />
        <Route path="/cardio" element={<Cardio />} />
        <Route path="/achievements" element={<Achievements />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/friends" element={<Friends />} />
        <Route path="/trainer" element={<Trainer />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/store-kit" element={<StoreKit />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}
